﻿import { Plugin } from 'ckeditor5';
import { ClickObserver, LinkEditing, ContextualBalloon, clickOutsideHandler, isWidget } from 'ckeditor5';
import { isLinkElement } from '@ckeditor/ckeditor5-link/src/utils';

import StatisticLink from './StatisticLink';
import LinkActionsView from './statisticLink/linkActionsView';

const VISUAL_SELECTION_MARKER_NAME = 'link-ui';

export default class StatisticLinkEditing extends Plugin {
	static get pluginName() { return "StatisticLinkEditing" }

	static get requires() { return [StatisticLink, LinkEditing, ContextualBalloon]; }

	get _isUIVisible() {
		return !!this.actionsView && this._balloon.visibleView == this.actionsView;
	}

	set _lastRange(range) {
		this.lastRange = range;
	}
	get _lastRange() {
		return this.lastRange;
	}

	init() {
		const editor = this.editor;
		editor.editing.view.addObserver(ClickObserver);
		this._balloon = editor.plugins.get(ContextualBalloon);
		this._enableBallonActivators();

		// Renders a fake visual selection marker on an expanded selection.
		editor.conversion.for('editingDowncast').markerToHighlight({
			model: VISUAL_SELECTION_MARKER_NAME,
			view: {
				classes: ['ck-fake-link-selection']
			}
		});

		// Renders a fake visual selection marker on a collapsed selection.
		editor.conversion.for('editingDowncast').markerToElement({
			model: VISUAL_SELECTION_MARKER_NAME,
			view: (data, { writer }) => {
				if (!data.markerRange.isCollapsed) {
					return null;
				}

				const markerElement = writer.createUIElement('span');

				writer.addClass(
					['ck-fake-link-selection', 'ck-fake-link-selection_collapsed'],
					markerElement
				);

				return markerElement;
			}
		});
	}

	_enableBallonActivators() {
		const editor = this.editor;
		const viewDocument = editor.editing.view.document;
		// Handle click on view document and show panel when selection is placed inside the link element.
		// Keep panel open until selection will be inside the same link element.
		this.listenTo(viewDocument, 'click', () => {
			const parentLink = this._getSelectedLinkElement(true);
			if (parentLink) {
				// Then show panel but keep focus inside editor editable.
				this._showUI();
			}
		});
		this.listenTo(viewDocument, 'keydown', () => {
			const parentLink = this._getSelectedLinkElement(true);
			if (parentLink) {
				// Then show panel but keep focus inside editor editable.
				this._showUI();
			} else {
				this._hideUI();
			}
		});
	}

	_showUI() {
		if (!this.actionsView) {
			this._createActionsView();
		}

		if (this._balloon.hasView(this.actionsView) === false) {
			this._balloon.add({
				view: this.actionsView,
				position: this._getBalloonPositionData()
			});
		}
	}

	_createActionsView() {
		const editor = this.editor;
		this.actionsView = new LinkActionsView(editor.locale);

		this.listenTo(this.actionsView, 'edit', () => {
			editor.execute('openLinkPopup');
		});

		this.listenTo(this.actionsView, 'unlink', () => {
			editor.execute('unlink');
			this._hideUI();
		});

		clickOutsideHandler({
			emitter: this.actionsView,
			activator: () => this._isUIVisible,
			contextElements: () => [this._balloon.view.element],
			callback: () => this._hideUI()
		});
	}

	_hideUI() {
		this.editor.editing.view.focus();
		if (this._balloon.hasView(this.actionsView) === true) {
			this._balloon.remove(this.actionsView);
		}
		this._hideFakeVisualSelection();
	}

	_showFakeVisualSelection() {
		const model = this.editor.model;

		model.change(writer => {
			const range = model.document.selection.getFirstRange();

			if (model.markers.has(VISUAL_SELECTION_MARKER_NAME)) {
				writer.updateMarker(VISUAL_SELECTION_MARKER_NAME, { range });
			} else {
				if (range.start.isAtEnd) {
					const startPosition = range.start.getLastMatchingPosition(
						({ item }) => !model.schema.isContent(item),
						{ boundaries: range }
					);

					writer.addMarker(VISUAL_SELECTION_MARKER_NAME, {
						usingOperation: false,
						affectsData: false,
						range: writer.createRange(startPosition, range.end)
					});
				} else {
					writer.addMarker(VISUAL_SELECTION_MARKER_NAME, {
						usingOperation: false,
						affectsData: false,
						range
					});
				}
			}
		});
	}

	_hideFakeVisualSelection() {
		const model = this.editor.model;

		if (model.markers.has(VISUAL_SELECTION_MARKER_NAME)) {
			model.change(writer => {
				writer.removeMarker(VISUAL_SELECTION_MARKER_NAME);
			});
		}
	}

	_getSelectedLinkElement(checkRange) {
		const view = this.editor.editing.view;
		const selection = view.document.selection;
		const selectedElement = selection.getSelectedElement();

		// The selection is collapsed or some widget is selected (especially inline widget).
		if (selection.isCollapsed || selectedElement && isWidget(selectedElement)) {
			this._lastRange = null;
			return findLinkElementAncestor(selection.getFirstPosition());
		} else {
			// The range for fully selected link is usually anchored in adjacent text nodes.
			// Trim it to get closer to the actual link element.
			const range = selection.getFirstRange().getTrimmed();
			const startLink = findLinkElementAncestor(range.start);
			const endLink = findLinkElementAncestor(range.end);

			if (checkRange === true) {
				if (this._lastRange && this._lastRange.isEqual(range)) {
					return null;
				}
			}
			this._lastRange = range;

			if (!startLink || startLink != endLink) {
				return null;
			}

			// Check if the link element is fully selected.
			if (view.createRangeIn(startLink).getTrimmed().isEqual(range)) {
				return startLink;
			} else {
				return null;
			}
		}
	}

	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const model = this.editor.model;
		const viewDocument = view.document;
		let target;
		if (model.markers.has(VISUAL_SELECTION_MARKER_NAME)) {
			// There are cases when we highlight selection using a marker (#7705, #4721).
			const markerViewElements = Array.from(this.editor.editing.mapper.markerNameToElements(VISUAL_SELECTION_MARKER_NAME));
			const newRange = view.createRange(view.createPositionBefore(markerViewElements[0]), view.createPositionAfter(markerViewElements[markerViewElements.length - 1]));
			target = view.domConverter.viewRangeToDom(newRange);
		}
		else {
			// Make sure the target is calculated on demand at the last moment because a cached DOM range
			// (which is very fragile) can desynchronize with the state of the editing view if there was
			// any rendering done in the meantime. This can happen, for instance, when an inline widget
			// gets unlinked.
			target = () => {
				const targetLink = this._getSelectedLinkElement();
				return targetLink ?
					// When selection is inside link element, then attach panel to this element.
					view.domConverter.mapViewToDom(targetLink) :
					// Otherwise attach panel to the selection.
					view.domConverter.viewRangeToDom(viewDocument.selection.getFirstRange());
			};
		}
		return { target };
	}

	destroy() {
		super.destroy();

		if (this.actionsView) {
			this.actionsView.destroy();
		}
	}
};

function findLinkElementAncestor(position) {
	return position.getAncestors().find((ancestor) => isLinkElement(ancestor)) || null;
}